/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {CheckCircleIcon} from '@salesforce/retail-react-app/app/components/icons';
import {Flex, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {usePasswordPolicy} from '@amblique/king-living/app/hooks/use-password-policy';
import {validatePassword} from '@amblique/king-living/app/utils/password-policy';

/**
 * UI component for styling password requirement line
 */
const PasswordRequirement = ({isValid, children, styles}) => {
  const iconStyles = {
    color: isValid ? 'brandColors.green' : 'greyscale.40',
  };

  const textStyles = {
    color: isValid ? 'brandColors.greyscale90' : 'brandColors.greyscale60',
  };


  return (
    <Flex {...styles.item}>
      <CheckCircleIcon {...styles.icon} {...iconStyles} />
      <Text {...styles.text} {...textStyles}>
        {children}
      </Text>
    </Flex>
  );
};

PasswordRequirement.propTypes = {
  /** Should it render in valid state */
  isValid: PropTypes.bool,

  /** The requirement text */
  children: PropTypes.any,

  /** The styles */
  styles: PropTypes.object,
};

/**
 * Per-rule password hints driven by Customer Login Settings (policy).
 * Labels use i18n with dynamic values; error messages use the BM site preference.
 */
const PasswordRequirements = ({value}) => {
  const {policy} = usePasswordPolicy();
  const pwValidations = validatePassword(value, policy);
  const styles = useMultiStyleConfig('PasswordRequirements', {});

  return (
    <Stack {...styles.container}>
      <PasswordRequirement isValid={pwValidations.hasMinChars} styles={styles}>
        <FormattedMessage
          defaultMessage="{minLength, plural, one {# Character} other {# Characters}}"
          description="Password requirement"
          id="password_requirements.error.minimum_characters"
          values={{minLength: policy.minLength}}
        />
      </PasswordRequirement>
      {policy.forceMixedCase && (
        <PasswordRequirement
          isValid={pwValidations.hasUppercase && pwValidations.hasLowercase}
          styles={styles}
        >
          <FormattedMessage
            defaultMessage="Upper and lower case letters"
            description="Password requirement"
            id="password_requirements.error.mixed_case"
          />
        </PasswordRequirement>
      )}
      {policy.forceLetters && !policy.forceMixedCase && (
        <PasswordRequirement isValid={pwValidations.hasLetters} styles={styles}>
          <FormattedMessage
            defaultMessage="1 letter"
            description="Password requirement"
            id="password_requirements.error.one_letter"
          />
        </PasswordRequirement>
      )}
      {policy.forceNumbers && (
        <PasswordRequirement isValid={pwValidations.hasNumber} styles={styles}>
          <FormattedMessage
            defaultMessage="1 number"
            description="Password requirement"
            id="password_requirements.error.one_number"
          />
        </PasswordRequirement>
      )}
      {policy.minSpecialChars > 0 && (
        <PasswordRequirement isValid={pwValidations.hasSpecialChar} styles={styles}>
          <FormattedMessage
            defaultMessage="{count, plural, one {# special character (e.g. $ ! % #)} other {# special characters (e.g. $ ! % #)}}"
            description="Password requirement"
            id="password_requirements.error.special_characters"
            values={{count: policy.minSpecialChars}}
          />
        </PasswordRequirement>
      )}
    </Stack>
  );
};

PasswordRequirements.propTypes = {
  /** The password to check against */
  value: PropTypes.string,
};

export default PasswordRequirements;
